import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Grid,
  Typography,
  Snackbar,
  Alert,
  AlertTitle,
  Link,
} from "@mui/material";
import { FinancingCard, getFinancingHeadingSx } from "../FinancingPresentation";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import CustomerReservationsClient from "../../../clients/CustomerReservationsClient";
import { getAutoFiLoanAppId } from "../../../shared/rules/FinancingRules";
import { getFinancingFieldDisplay } from "../../../shared/constants/FinancingConstants";
import { formatPriceValue } from "../../../utils/FormatPrice";
import ActionRequiredIcon from "../../../shared/Icons/ActionRequiredIcon";
import EMPLoader from "../../../shared/components/EMPLoader/EMPLoader";
import CachedIcon from "@mui/icons-material/Cached";
import { useTranslation } from "react-i18next";
import { track3pfLoadDetailsClick } from "../../../shared/services/analytics/OrderDetailsPageAnalytics";
import { trackReferralExit } from "../../../shared/services/analytics/AnalyticsService";
import { useVehicleData } from "../../../shared/hooks/useVehicleData";

const formatPercentageValue = (key, value) => {
  if (!value) {
    return "--";
  }

  if (key === "buyRate") {
    return `${(Number.parseFloat(value) * 100).toFixed(2)}%`;
  }

  return `${Number.parseFloat(value).toFixed(2)}%`;
};

const ThirdPartyFinancingTable = ({
  data: initialData,
  orderId,
  user,
  isLincolnBrand = false,
}) => {
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const loanAppId = getAutoFiLoanAppId(orderId);
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { modelYear, nameplate } = useVehicleData(orderId);

  const handleSnackbarClose = () => {
    setIsSnackbarOpen(false);
    // Reset the query's error state when closing the snackbar
    queryClient.resetQueries(
      {
        queryKey: ["financeLoanDetails", orderId, loanAppId],
      },
      { cancelRefetch: false }
    );
  };

  const {
    data: queryData,
    refetch: refreshFinanceDetails,
    isFetching: isRefreshing,
    isError,
  } = useQuery({
    queryKey: ["financeLoanDetails", orderId, loanAppId],
    queryFn: async () => {
      try {
        const response =
          await CustomerReservationsClient.getFinanceLoanAppDetails(
            orderId,
            loanAppId
          );

        const transformedData = Object.entries(response).reduce(
          (obj, [key, value]) => {
            if (key !== "stipulations") {
              let formattedValue = value;

              // Format currency values
              if (
                ["monthlyPayment", "downPayment", "amountFinanced"].includes(
                  key
                )
              ) {
                formattedValue = formatPriceValue(value);
              }
              // Format percentage values
              else if (["buyRate", "apr"].includes(key)) {
                formattedValue = formatPercentageValue(key, value);
              }

              obj[key] = {
                heading: getFinancingFieldDisplay(key),
                content: formattedValue,
              };
            }
            return obj;
          },
          {}
        );

        return transformedData;
      } catch (error) {
        console.error("Error fetching finance loan details:", error);
        throw error; // Re-throw the error so react-query can handle it
      }
    },
    initialData: initialData,
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    enabled: false,
    retry: 1,
  });

  // Merge queryData with initialData when queryData is available
  const displayData = queryData
    ? { ...initialData, ...queryData }
    : initialData;

  useEffect(() => {
    if (isError) {
      setIsSnackbarOpen(true);
    }
  }, [isError]);

  const handleRefreshFinanceDetails = () => {
    track3pfLoadDetailsClick(orderId, user, modelYear, nameplate);
    refreshFinanceDetails();
  };

  const refreshClickHandler = isRefreshing
    ? undefined
    : handleRefreshFinanceDetails;

  return (
    <>
      <Box gridColumn={{ xs: "span 12" }}>
        <Grid container spacing={4}>
          <Grid item>
            <Typography
              variant="h3"
              sx={getFinancingHeadingSx(isLincolnBrand)}
              data-testid="payment-type-heading"
            >
              {t("financing.thirdPartyFinancing.heading")}
            </Typography>
          </Grid>
          {loanAppId && (
            <Grid
              item
              container
              xs={12}
              spacing={1}
              justifyContent="flex-start"
              alignItems="center"
            >
              <Grid item>
                <Typography variant="body2">
                  {t("financing.thirdPartyFinancing.subHeading")}{" "}
                  <Box
                    component="span"
                    role="button"
                    aria-label="Refresh financing details"
                    sx={{
                      color: isRefreshing ? "#666666" : "primary.main",
                      cursor: isRefreshing ? "default" : "pointer",
                      textDecoration: "underline",
                      pointerEvents: isRefreshing ? "none" : "auto",
                    }}
                    onClick={refreshClickHandler}
                  >
                    {t("common.buttonCtas.clickHere")}
                  </Box>
                </Typography>
              </Grid>
              <Grid item>
                <CachedIcon
                  fontSize="inherit"
                  color="inherit"
                  sx={{
                    verticalAlign: "middle",
                    borderRadius: "50%",
                    padding: "2px",
                    backgroundColor: "#000000",
                    color: "#ffffff",
                    animation: isRefreshing
                      ? "spin 1s linear infinite"
                      : "none",
                    "@keyframes spin": {
                      "0%": {
                        transform: "rotate(0deg)",
                      },
                      "100%": {
                        transform: "rotate(360deg)",
                      },
                    },
                    "&:hover": {
                      backgroundColor: "#000000",
                      color: "#ffffff",
                    },
                  }}
                />
              </Grid>
            </Grid>
          )}
        </Grid>
      </Box>
      <Box gridColumn={{ xs: "span 12" }}>
        {!displayData || isRefreshing ? (
          <Box
            sx={{
              width: "100%",
              height: "200px",
              backgroundColor: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <EMPLoader
              loadingMessage={t("financing.thirdPartyFinancing.loadingMessage")}
            />
          </Box>
        ) : (
          <FinancingCard data={displayData} isLincolnBrand={isLincolnBrand} />
        )}
      </Box>
      <Snackbar
        open={isSnackbarOpen}
        onClose={(event, reason) => {
          if (reason !== "clickaway") {
            handleSnackbarClose();
          }
        }}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        disableWindowBlurListener
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="warning"
          variant="filled"
          sx={{ p: 4 }}
          icon={<ActionRequiredIcon />}
          role="alert"
          aria-describedby="financing-error-description"
        >
          <AlertTitle variant="subtitle1" component="p" sx={{ mb: 0, pt: 0 }}>
            {t("common.somethingWentWrong")}
          </AlertTitle>
          <Typography
            variant="body2"
            id="financing-error-description"
            sx={{ display: "inline" }}
          >
            {t("common.checkBackLater")}{" "}
          </Typography>
          <Link
            variant="body2"
            href="//www.routeone.net/"
            underline="always"
            target="_blank"
            rel="noreferrer"
            color="text.primary"
            onClick={() =>
              trackReferralExit("https://www.routeone.net", orderId, user)
            }
          >
            {t("common.buttonCtas.routeOne")}.
          </Link>
        </Alert>
      </Snackbar>
    </>
  );
};

ThirdPartyFinancingTable.propTypes = {
  isLincolnBrand: PropTypes.bool,
  data: PropTypes.objectOf(
    PropTypes.shape({
      heading: PropTypes.string,
      content: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.node,
      ]),
    })
  ),
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  user: PropTypes.shape({
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    market: PropTypes.string,
    dealerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    salesCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    brand: PropTypes.string,
  }).isRequired,
};

ThirdPartyFinancingTable.defaultProps = {
  data: null,
};

export default ThirdPartyFinancingTable;
